export const WHATSAPP_CONVERSATION_ACCESS = {
    RECORD_NAME: 'whatsapp_conversations',
    CODIGO_REGISTRO: 'whatsapp_conversation',
    CODIGO_MODULO: 'notificacion',
    NOMBRE_SINGULAR: 'Inbox WhatsApp',
    NOMBRE_PLURAL: 'Inbox WhatsApp',
} as const;

export const WHATSAPP_CONVERSATION_SPECIAL_PERMISSION = {
    MANAGE_ASSIGNMENT: {
        codigo: 'gestionar_asignacion_whatsapp_inbox',
        nombre: 'Gestionar asignación de WhatsApp Inbox',
    },
} as const;

export const WHATSAPP_CONVERSATION_STATUS = {
    PENDIENTE: 1,
    EN_ATENCION: 2,
    RESUELTO: 3,
    CERRADO: 4,
} as const;

export type WhatsappConversationStatusCode = (typeof WHATSAPP_CONVERSATION_STATUS)[keyof typeof WHATSAPP_CONVERSATION_STATUS];

export const WHATSAPP_CONVERSATION_UI_STATUS = {
    PENDIENTE: 'pendiente',
    EN_ATENCION: 'en_atencion',
    DERIVADO: 'derivado',
    RESUELTO: 'resuelto',
} as const;

export type WhatsappConversationUiStatusCode = (typeof WHATSAPP_CONVERSATION_UI_STATUS)[keyof typeof WHATSAPP_CONVERSATION_UI_STATUS];

export const WHATSAPP_CONVERSATION_DIRECTION = {
    INBOUND: 1,
    OUTBOUND: 2,
} as const;

export type WhatsappConversationDirectionCode = (typeof WHATSAPP_CONVERSATION_DIRECTION)[keyof typeof WHATSAPP_CONVERSATION_DIRECTION];

export const WHATSAPP_MESSAGE_TYPE = {
    TEMPLATE: 1,
    TEXT: 2,
    MEDIA: 3,
    SYSTEM: 4,
    /** Tap de botón/lista interactiva (inbound) o envío del motor de flujos (outbound). Espejo de `MESSAGE_TYPE.INTERACTIVE` en `whatsapp_conversation.service.ts`. */
    INTERACTIVE: 5,
} as const;

export type WhatsappMessageTypeCode = (typeof WHATSAPP_MESSAGE_TYPE)[keyof typeof WHATSAPP_MESSAGE_TYPE];

/**
 * Códigos de evento propios del motor de flujos (S3b `motor.service.ts`,
 * `EVENT_TYPE_FLUJO_INICIADO`/`EVENT_TYPE_FLUJO_PAUSADO`). El backend ya
 * traduce `event_type_code` a `event_type_label` para el resto de los
 * eventos (`EVENT_LABELS` en `whatsapp_conversation.service.ts`), pero ese
 * mapa todavía no conoce estos dos códigos y devuelve `'desconocido'` —
 * `getWhatsappEventTypeLabel` los traduce acá sin tocar el backend.
 */
export const WHATSAPP_FLOW_EVENT_TYPE = {
    FLUJO_INICIADO: 12,
    FLUJO_PAUSADO: 13,
} as const;

export type WhatsappFlowEventTypeCode = (typeof WHATSAPP_FLOW_EVENT_TYPE)[keyof typeof WHATSAPP_FLOW_EVENT_TYPE];

const WHATSAPP_FLOW_EVENT_LABELS: Record<number, string> = {
    [WHATSAPP_FLOW_EVENT_TYPE.FLUJO_INICIADO]: 'Flujo iniciado',
    [WHATSAPP_FLOW_EVENT_TYPE.FLUJO_PAUSADO]: 'Flujo pausado',
};

export const WHATSAPP_DELIVERY_STATUS = {
    PENDIENTE_ENVIO: 1,
    ENVIADO: 2,
    FALLIDO: 4,
} as const;

export type WhatsappDeliveryStatusCode = (typeof WHATSAPP_DELIVERY_STATUS)[keyof typeof WHATSAPP_DELIVERY_STATUS];

export const WHATSAPP_EXTERNAL_HANDOFF_STATUS = {
    NO_DERIVADA: 0,
    DERIVADA_FUERA: 1,
    RETOMADA_INTERNA: 2,
} as const;

export type WhatsappExternalHandoffStatusCode = (typeof WHATSAPP_EXTERNAL_HANDOFF_STATUS)[keyof typeof WHATSAPP_EXTERNAL_HANDOFF_STATUS];

export interface WhatsappConversationReplyTemplate {
    id: number;
    id_empresa: number;
    nombre: string;
    mensaje: string;
    estado: number | null;
    approve_status: string | null;
    language: string | null;
    /** Identidad de la plantilla en Meta: es lo que se manda al responder. */
    meta_template_name: string | null;
    /** Heredado de Twilio. Se conserva mientras existan plantillas sin recrear. */
    content_sid: string | null;
    url_imagen: string | null;
}

export interface WhatsappConversationWindowState {
    isOutsideWindow: boolean | null;
    requiresTemplateReply: boolean;
    canSendFreeReply: boolean;
    expiresAt: string | Date | null;
    source: 'backend' | 'derived' | 'unknown';
    label: string;
    severity: string;
    detail: string;
}

export interface WhatsappConversationFilter {
    idEmpresa?: number;
    page?: number;
    size?: number;
    search?: string;
    conversationId?: number | null;
    conversationStartFrom?: string | Date | null;
    conversationStartTo?: string | Date | null;
    uiStatus?: WhatsappConversationUiStatusCode | null;
    assignedUserId?: number | null;
    onlyUnread?: boolean;
    onlyUnassigned?: boolean;
}

export interface WhatsappConversationDetailFilter {
    idEmpresa?: number;
    markAsRead?: boolean;
}

export interface WhatsappConversationSummary {
    id: number;
    id_empresa: number;
    nombre_empresa: string | null;
    nombre: string;
    contact_name?: string | null;
    customer_phone: string;
    customer_phone_normalized: string;
    historial_notificacion_id: number | null;
    job_procesamiento_id: number | null;
    assigned_user_id: number | null;
    assigned_user_name: string | null;
    status_code: number;
    status_label: string;
    unread_count: number;
    last_message_at: string | Date | null;
    last_message_preview: string | null;
    last_message_direction_code: number | null;
    first_inbound_at: string | Date | null;
    conversation_started_at?: string | Date | null;
    first_agent_reply_at: string | Date | null;
    assigned_at: string | Date | null;
    resolved_at: string | Date | null;
    closed_at: string | Date | null;
    external_handoff_status_code: number | null;
    external_handoff_status_label?: string | null;
    external_handoff_reason?: string | null;
    external_handoff_target_phone?: string | null;
    external_handoff_responsible_name?: string | null;
    external_handoff_responsible_user_id?: number | null;
    external_handoff_summary?: string | null;
    external_handoff_at?: string | Date | null;
    last_inbound_at?: string | Date | null;
    conversation_window_expires_at?: string | Date | null;
    is_outside_window?: boolean | null;
    is_within_window?: boolean | null;
    reply_requires_template?: boolean | null;
    can_send_free_reply?: boolean | null;
    metadata?: Record<string, unknown> | null;
    available_reply_templates?: WhatsappConversationReplyTemplate[] | null;
    historial_nombre: string | null;
}

export interface WhatsappConversationMessage {
    id: number;
    id_empresa: number;
    conversation_id: number;
    actor_user_id: number | null;
    actor_user_name: string | null;
    direction_code: number;
    message_type_code: number;
    delivery_status_code: number | null;
    provider_message_sid: string | null;
    provider_status_raw: string | null;
    provider_error_detail: string | null;
    from_phone: string | null;
    to_phone: string | null;
    body_text: string | null;
    media_url: string | null;
    media_content_type: string | null;
    template_id?: number | null;
    template_code?: string | null;
    template_language?: string | null;
    metadata?: Record<string, unknown> | null;
    fecha_operacion: string | Date | null;
}

export interface WhatsappConversationEvent {
    id: number;
    id_empresa: number;
    conversation_id: number;
    message_id: number | null;
    actor_user_id: number | null;
    actor_user_name: string | null;
    from_assigned_user_id: number | null;
    from_assigned_user_name: string | null;
    to_assigned_user_id: number | null;
    to_assigned_user_name: string | null;
    event_type_code: number;
    event_type_label: string;
    summary: string;
    fecha_operacion: string | Date | null;
}

export interface WhatsappConversationDetailResponse {
    conversation: WhatsappConversationSummary;
    messages: WhatsappConversationMessage[];
    events: WhatsappConversationEvent[];
    available_reply_templates?: WhatsappConversationReplyTemplate[];
}

export interface WhatsappConversationReplyPayload {
    body_text?: string;
    media_url?: string;
    template_id?: number | null;
    template_code?: string | null;
    template_language?: string | null;
}

export interface WhatsappConversationExternalHandoffPayload {
    target_phone: string;
    responsible_name: string;
    summary: string;
}

export interface WhatsappAssignableUser {
    id: number;
    nombre: string;
    codigo: string;
    display_label?: string;
    id_empresa: number;
    nombre_empresa: string;
    activo: boolean;
}

export const WHATSAPP_CONVERSATION_STATUS_OPTIONS = [
    { valor: WHATSAPP_CONVERSATION_STATUS.PENDIENTE, nombre: 'Pendiente' },
    { valor: WHATSAPP_CONVERSATION_STATUS.EN_ATENCION, nombre: 'En atención' },
    { valor: WHATSAPP_CONVERSATION_STATUS.RESUELTO, nombre: 'Resuelto' },
];

export const WHATSAPP_CONVERSATION_UI_STATUS_OPTIONS = [
    { valor: WHATSAPP_CONVERSATION_UI_STATUS.PENDIENTE, nombre: 'Pendiente' },
    { valor: WHATSAPP_CONVERSATION_UI_STATUS.EN_ATENCION, nombre: 'En Atención' },
    { valor: WHATSAPP_CONVERSATION_UI_STATUS.DERIVADO, nombre: 'Derivado' },
    { valor: WHATSAPP_CONVERSATION_UI_STATUS.RESUELTO, nombre: 'Resuelto' },
];

export const getWhatsappConversationStatusLabel = (statusCode: number | null | undefined): string => {
    if (Number(statusCode || 0) === WHATSAPP_CONVERSATION_STATUS.CERRADO) {
        return 'Resuelto';
    }

    const option = WHATSAPP_CONVERSATION_STATUS_OPTIONS.find((item) => item.valor === Number(statusCode || 0));
    return option?.nombre || 'Sin estado';
};

export const getWhatsappConversationStatusSeverity = (statusCode: number | null | undefined): string => {
    switch (Number(statusCode || 0)) {
        case WHATSAPP_CONVERSATION_STATUS.PENDIENTE:
            return 'warning';
        case WHATSAPP_CONVERSATION_STATUS.EN_ATENCION:
            return 'info';
        case WHATSAPP_CONVERSATION_STATUS.RESUELTO:
        case WHATSAPP_CONVERSATION_STATUS.CERRADO:
            return 'success';
        default:
            return 'info';
    }
};

export const getWhatsappExternalHandoffStatusLabel = (statusCode: number | null | undefined): string => {
    switch (Number(statusCode ?? WHATSAPP_EXTERNAL_HANDOFF_STATUS.NO_DERIVADA)) {
        case WHATSAPP_EXTERNAL_HANDOFF_STATUS.DERIVADA_FUERA:
            return 'Derivado';
        case WHATSAPP_EXTERNAL_HANDOFF_STATUS.RETOMADA_INTERNA:
            return 'Retomada interna';
        default:
            return 'Sin derivación';
    }
};

export const getWhatsappExternalHandoffStatusSeverity = (statusCode: number | null | undefined): string => {
    switch (Number(statusCode ?? WHATSAPP_EXTERNAL_HANDOFF_STATUS.NO_DERIVADA)) {
        case WHATSAPP_EXTERNAL_HANDOFF_STATUS.DERIVADA_FUERA:
            return 'warning';
        case WHATSAPP_EXTERNAL_HANDOFF_STATUS.RETOMADA_INTERNA:
            return 'success';
        default:
            return 'info';
    }
};

export interface WhatsappConversationUiStatusDescriptor {
    code: WhatsappConversationUiStatusCode;
    label: string;
    severity: string;
}

export const getWhatsappConversationUiStatus = (
    conversation: Partial<WhatsappConversationSummary> | null | undefined,
): WhatsappConversationUiStatusDescriptor => {
    if (Number(conversation?.external_handoff_status_code || 0) === WHATSAPP_EXTERNAL_HANDOFF_STATUS.DERIVADA_FUERA) {
        return {
            code: WHATSAPP_CONVERSATION_UI_STATUS.DERIVADO,
            label: 'Derivado',
            severity: 'warning',
        };
    }

    switch (Number(conversation?.status_code || 0)) {
        case WHATSAPP_CONVERSATION_STATUS.PENDIENTE:
            return {
                code: WHATSAPP_CONVERSATION_UI_STATUS.PENDIENTE,
                label: 'Pendiente',
                severity: 'warning',
            };
        case WHATSAPP_CONVERSATION_STATUS.EN_ATENCION:
            return {
                code: WHATSAPP_CONVERSATION_UI_STATUS.EN_ATENCION,
                label: 'En Atención',
                severity: 'info',
            };
        case WHATSAPP_CONVERSATION_STATUS.CERRADO:
        case WHATSAPP_CONVERSATION_STATUS.RESUELTO:
            return {
                code: WHATSAPP_CONVERSATION_UI_STATUS.RESUELTO,
                label: 'Resuelto',
                severity: 'success',
            };
        default:
            return {
                code: WHATSAPP_CONVERSATION_UI_STATUS.PENDIENTE,
                label: 'Pendiente',
                severity: 'warning',
            };
    }
};

export const getWhatsappConversationDerivedStatusDetail = (
    conversation: Partial<WhatsappConversationSummary> | null | undefined,
): string | null => {
    if (Number(conversation?.external_handoff_status_code || 0) !== WHATSAPP_EXTERNAL_HANDOFF_STATUS.DERIVADA_FUERA) {
        return null;
    }

    const derivedResponsibleName = String(conversation?.external_handoff_responsible_name || '').trim();
    const derivedTargetPhone = String(conversation?.external_handoff_target_phone || '').trim();
    const derivedDetailParts = [derivedResponsibleName, derivedTargetPhone].filter((value) => Boolean(value));

    if (derivedDetailParts.length === 0) {
        return null;
    }

    return `A ${derivedDetailParts.join(' · ')}`;
};

export const getWhatsappConversationDirectionLabel = (directionCode: number | null | undefined): string => {
    return Number(directionCode || 0) === WHATSAPP_CONVERSATION_DIRECTION.OUTBOUND ? 'Salida' : 'Entrada';
};

export const getWhatsappConversationDirectionIcon = (directionCode: number | null | undefined): string => {
    return Number(directionCode || 0) === WHATSAPP_CONVERSATION_DIRECTION.OUTBOUND ? 'pi pi-send' : 'pi pi-download';
};

export const getWhatsappDeliveryStatusLabel = (deliveryStatusCode: number | null | undefined): string => {
    switch (Number(deliveryStatusCode || 0)) {
        case WHATSAPP_DELIVERY_STATUS.PENDIENTE_ENVIO:
            return 'Pendiente';
        case WHATSAPP_DELIVERY_STATUS.ENVIADO:
            return 'Enviado';
        case WHATSAPP_DELIVERY_STATUS.FALLIDO:
            return 'Fallido';
        default:
            return 'N/A';
    }
};

export const getWhatsappDeliveryStatusSeverity = (deliveryStatusCode: number | null | undefined): string => {
    switch (Number(deliveryStatusCode || 0)) {
        case WHATSAPP_DELIVERY_STATUS.PENDIENTE_ENVIO:
            return 'warning';
        case WHATSAPP_DELIVERY_STATUS.ENVIADO:
            return 'success';
        case WHATSAPP_DELIVERY_STATUS.FALLIDO:
            return 'danger';
        default:
            return 'info';
    }
};

export const getWhatsappMessageTypeLabel = (messageTypeCode: number | null | undefined): string => {
    switch (Number(messageTypeCode || 0)) {
        case WHATSAPP_MESSAGE_TYPE.TEMPLATE:
            return 'Template';
        case WHATSAPP_MESSAGE_TYPE.TEXT:
            return 'Texto';
        case WHATSAPP_MESSAGE_TYPE.MEDIA:
            return 'Media';
        case WHATSAPP_MESSAGE_TYPE.SYSTEM:
            return 'Sistema';
        case WHATSAPP_MESSAGE_TYPE.INTERACTIVE:
            return 'Interactivo';
        default:
            return 'Mensaje';
    }
};

/**
 * Etiqueta de un evento de conversación. Para los dos códigos del motor de
 * flujos usa la traducción propia de acá; para cualquier otro código confía
 * en `event_type_label` que ya viene resuelto desde el backend (`EVENT_LABELS`
 * en `whatsapp_conversation.service.ts`) — nunca reimplementa esa tabla.
 */
export const getWhatsappEventTypeLabel = (event: Pick<WhatsappConversationEvent, 'event_type_code' | 'event_type_label'>): string => {
    return WHATSAPP_FLOW_EVENT_LABELS[Number(event.event_type_code || 0)] || event.event_type_label || 'Evento';
};

export const getWhatsappEventTypeSeverity = (eventTypeCode: number | null | undefined): string => {
    switch (Number(eventTypeCode || 0)) {
        case WHATSAPP_FLOW_EVENT_TYPE.FLUJO_INICIADO:
            return 'info';
        case WHATSAPP_FLOW_EVENT_TYPE.FLUJO_PAUSADO:
            return 'warning';
        default:
            return 'info';
    }
};

const coerceBoolean = (value: unknown): boolean | null => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value === 1 ? true : value === 0 ? false : null;
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', '1', 'si', 'sí', 'yes'].includes(normalized)) {
            return true;
        }

        if (['false', '0', 'no'].includes(normalized)) {
            return false;
        }
    }

    return null;
};

const resolveWindowDate = (value: unknown): string | Date | null => {
    if (!value) {
        return null;
    }

    return value as string | Date;
};

export const getWhatsappConversationWindowState = (conversation: Partial<WhatsappConversationSummary> | null | undefined): WhatsappConversationWindowState => {
    const rawOutsideWindow = coerceBoolean(
        conversation?.is_outside_window
        ?? conversation?.metadata?.['is_outside_window']
        ?? conversation?.metadata?.['outside_window']
        ?? conversation?.metadata?.['fuera_de_ventana']
    );
    const rawWithinWindow = coerceBoolean(
        conversation?.is_within_window
        ?? conversation?.metadata?.['is_within_window']
        ?? conversation?.metadata?.['within_window']
        ?? conversation?.metadata?.['dentro_de_ventana']
    );
    const rawRequiresTemplate = coerceBoolean(
        conversation?.reply_requires_template
        ?? conversation?.metadata?.['reply_requires_template']
        ?? conversation?.metadata?.['requires_template_reply']
        ?? conversation?.metadata?.['template_required']
    );
    const rawCanSendFreeReply = coerceBoolean(
        conversation?.can_send_free_reply
        ?? conversation?.metadata?.['can_send_free_reply']
        ?? conversation?.metadata?.['allow_free_reply']
    );

    const expiresAt = resolveWindowDate(
        conversation?.conversation_window_expires_at
        ?? conversation?.metadata?.['conversation_window_expires_at']
        ?? conversation?.metadata?.['window_expires_at']
        ?? conversation?.metadata?.['reply_window_expires_at']
    );

    let isOutsideWindow = rawOutsideWindow;
    let source: WhatsappConversationWindowState['source'] = 'unknown';

    if (isOutsideWindow === null && rawWithinWindow !== null) {
        isOutsideWindow = !rawWithinWindow;
        source = 'backend';
    } else if (isOutsideWindow !== null) {
        source = 'backend';
    }

    if (expiresAt) {
        const expiresAtDate = new Date(expiresAt as string | Date);
        if (!Number.isNaN(expiresAtDate.getTime()) && (isOutsideWindow === null || expiresAtDate.getTime() <= Date.now())) {
            isOutsideWindow = expiresAtDate.getTime() <= Date.now();
            source = 'derived';
        }
    }

    const requiresTemplateReply = isOutsideWindow === true
        ? true
        : rawRequiresTemplate !== null
            ? rawRequiresTemplate
            : false;

    const canSendFreeReply = isOutsideWindow === true
        ? false
        : rawCanSendFreeReply !== null
            ? rawCanSendFreeReply
            : !requiresTemplateReply;

    if (isOutsideWindow === true) {
        return {
            isOutsideWindow,
            requiresTemplateReply,
            canSendFreeReply,
            expiresAt,
            source,
            label: 'Fuera de ventana',
            severity: 'warning',
            detail: 'Solo se permite responder con template aprobado.',
        };
    }

    if (isOutsideWindow === false) {
        return {
            isOutsideWindow,
            requiresTemplateReply,
            canSendFreeReply,
            expiresAt,
            source,
            label: 'Ventana abierta',
            severity: 'success',
            detail: 'Se permite reply libre mientras la ventana siga abierta.',
        };
    }

    return {
        isOutsideWindow,
        requiresTemplateReply,
        canSendFreeReply,
        expiresAt,
        source,
        label: 'Estado de envío por confirmar',
        severity: 'info',
        detail: 'Todavía no pudimos confirmar si la conversación acepta mensaje libre. Para ir a lo seguro, usá un template aprobado.',
    };
};
